import { extractApiItems } from './extract-api-items.js';
import { mapApiItemsToOptions } from './map-options-response.js';
import { buildOptionsUrl } from './build-options-url.js';
import { isAbortError } from '../http/is-abort-error.js';

/**
 * Configuration for the API options lookup factory.
 *
 * @typedef {Object} ApiOptionsLookupConfig
 * @property {string} url - Same `/api/{entity}/options` endpoint as the loader.
 * @property {string} [valueProp]
 * @property {string} [labelProp]
 * @property {string} [iconProp]
 * @property {string} [iconAs]
 */

/**
 * Normalizes lookup input: String-coerce, drop empties, dedupe (first-seen
 * order), cap to 200. Mirrors the backend's input handling.
 *
 * @param {ReadonlyArray<string|number>} values
 * @returns {string[]}
 */
function normalizeLookupValues(values) {
  /** @type {string[]} */
  const out = [];
  /** @type {Set<string>} */
  const seen = new Set();
  for (const v of values) {
    if (v == null) continue;
    const s = String(v);
    if (s === '' || seen.has(s)) continue;
    seen.add(s);
    out.push(s);
    if (out.length >= 200) break;
  }
  return out;
}

/**
 * Creates an exact-value `OptionsLookupFn` for the `/options?values=` API.
 *
 * Lookup is identity hydration only: it bypasses search (`q`), the result cap
 * (`limit`), and dependent-filter params. The mock backend (`public/api/index.php`
 * `/options` GET handler) enters lookup mode by presence of the `values` key.
 *
 * The returned function is contract-conformant per `OptionsLookupFn`:
 *   - input order preserved (after dedupe);
 *   - missing IDs absent from response;
 *   - extra/duplicate backend rows filtered out client-side.
 *
 * @param {import('../../contracts/api.js').ApiClient} apiClient
 * @param {ApiOptionsLookupConfig} config
 * @param {{
 *   logger?: { error: (msg: string, ctx?: { data?: unknown }) => void },
 * }} [deps]
 * @returns {import('../../contracts/forms.js').OptionsLookupFn}
 */
export function createApiOptionsLookup(apiClient, config, deps) {
  const { url, valueProp = 'value', labelProp = 'label', iconProp, iconAs } = config;
  const logger = deps?.logger;

  return async (values, opts) => {
    if (!Array.isArray(values) || values.length === 0) {
      return [];
    }
    const normalized = normalizeLookupValues(values);
    if (normalized.length === 0) {
      return [];
    }

    const parts = normalized.map(v => `values=${encodeURIComponent(v)}`);
    const finalUrl = buildOptionsUrl(url, parts);

    let response;
    try {
      response = await apiClient.get(finalUrl, { signal: opts?.signal });
    } catch (err) {
      if (isAbortError(err)) throw err;
      logger?.error?.('Options lookup failed', {
        data: { url: finalUrl, count: normalized.length, err },
      });
      throw err;
    }

    const items = extractApiItems(response);
    const mapped = mapApiItemsToOptions(items, { valueProp, labelProp, iconProp, iconAs });

    // Defensive: dedupe response by value (last-wins) and emit in input order,
    // dropping any rows the backend returned for unrequested values.
    /** @type {Map<string, import('../../contracts/forms.js').FieldOption>} */
    const byValue = new Map();
    for (const opt of mapped) byValue.set(opt.value, opt);

    /** @type {import('../../contracts/forms.js').FieldOption[]} */
    const result = [];
    for (const v of normalized) {
      const opt = byValue.get(v);
      if (opt) result.push(opt);
    }
    return result;
  };
}
